/**
 * Production-only last-resort handlers for unexpected window errors / rejections.
 * Not a replacement for page-level error states or AppErrorBoundary.
 */

import { isChunkLoadFailure } from "@/bootstrap/install-chunk-error-handler";
import {
  shouldIgnorePanicErrorEvent,
  shouldIgnorePanicRejection,
} from "@/bootstrap/panic-noise-filter";
import { shouldInstallProductionHandlers } from "@/bootstrap/production-handler-gate";
import { renderMinimalFallbackShell } from "@/theme/startup-safe-render";

let installed = false;
let rendering = false;

function safePaintPanicShell(): void {
  if (rendering) return;
  rendering = true;
  try {
    renderMinimalFallbackShell();
  } catch (paintError) {
    // eslint-disable-next-line no-console -- last-resort containment
    console.error("[panic] failed to paint fallback shell", paintError);
  } finally {
    // Keep latch set so recursive window errors cannot loop paint attempts.
    rendering = true;
  }
}

function onWindowError(event: ErrorEvent): void {
  try {
    // Chunk mismatches are owned by installChunkErrorHandler.
    if (isChunkLoadFailure(event.error ?? event.message)) return;
    if (shouldIgnorePanicErrorEvent(event)) return;

    // eslint-disable-next-line no-console -- last-resort containment
    console.error("[panic] window error", event.error ?? event.message);
    safePaintPanicShell();
  } catch {
    // never throw from handler
  }
}

function onUnhandledRejection(event: PromiseRejectionEvent): void {
  try {
    if (isChunkLoadFailure(event.reason)) return;
    if (shouldIgnorePanicRejection(event.reason)) return;

    // eslint-disable-next-line no-console -- last-resort containment
    console.error("[panic] unhandledrejection", event.reason);
    safePaintPanicShell();
  } catch {
    // never throw from handler
  }
}

/** Register production-only global panic listeners. Idempotent. */
export function installGlobalPanicHandlers(): void {
  if (!shouldInstallProductionHandlers()) return;
  if (typeof window === "undefined") return;
  if (installed) return;
  installed = true;

  window.addEventListener("error", onWindowError);
  window.addEventListener("unhandledrejection", onUnhandledRejection);
}

/** Test-only reset for Jest isolation. */
export function resetGlobalPanicHandlersForTests(): void {
  if (typeof window !== "undefined" && installed) {
    window.removeEventListener("error", onWindowError);
    window.removeEventListener("unhandledrejection", onUnhandledRejection);
  }
  installed = false;
  rendering = false;
}
